// =============================================================================
// Origin UI — src/pages/admin/CardApproverAdminTab.tsx
// Admin Config → Credit Cards → Approvers tab.
// Manage card-to-approver mappings with sort, filter, search.
// CC-11.2 v2
// =============================================================================

import { useState, useEffect, useMemo, useRef } from 'react';
import { api } from '@/services/apiService';
import { Card, ErrorBanner, LoadingSpinner, ConfirmDialog } from '@/components/common';
import {
  getApprovers, getCards, createApprover, deleteApprover,
  type CardApprover, type CorporateCardListItem
} from '@/services/creditCardApi';
import styles from './admin.module.css';

interface EmployeeOption { employeeId: string; fullName: string; employeeNumber: string; }

type SortKey = 'cardholderName' | 'cardIssuer' | 'approverName';
type SortDir = 'asc' | 'desc';

export function CardApproverAdminTab() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvers, setApprovers] = useState<CardApprover[]>([]);
  const [cards, setCards] = useState<CorporateCardListItem[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ corporateCardId: '', approverEmployeeId: '' });

  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('cardholderName');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterCardholder, setFilterCardholder] = useState('');
  const [filterApprover, setFilterApprover] = useState('');

  // Searchable dropdowns
  const [cardSearch, setCardSearch] = useState('');
  const [cardDropdownOpen, setCardDropdownOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [apprSearch, setApprSearch] = useState('');
  const [apprDropdownOpen, setApprDropdownOpen] = useState(false);
  const apprRef = useRef<HTMLDivElement>(null);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) setCardDropdownOpen(false);
      if (apprRef.current && !apprRef.current.contains(e.target as Node)) setApprDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  async function load() {
    try {
      setLoading(true);
      const [a, c, e] = await Promise.all([getApprovers(), getCards(), api.get<EmployeeOption[]>('/api/employees?minimal=true')]);
      setApprovers(a);
      setCards(c.sort((a, b) => (a.employeeName || '').localeCompare((b.employeeName || ''))));
      setEmployees(e.map(emp => ({ ...emp, fullName: (((emp as any).firstName || '') + ' ' + ((emp as any).lastName || '')).trim() || emp.fullName || '' })).filter(emp => emp.fullName.trim() !== '').sort((a, b) => a.fullName.localeCompare(b.fullName)));
    } catch (e: any) { setError(e.message); }
    finally { setLoading(false); }
  }

  const filteredApprovers = useMemo(() => {
    let data = approvers;
    if (filterCardholder) data = data.filter(a => (a.cardholderName || '').toLowerCase().includes(filterCardholder.toLowerCase()));
    if (filterApprover) data = data.filter(a => (a.approverName || '').toLowerCase().includes(filterApprover.toLowerCase()));
    return [...data].sort((a, b) => {
      const cmp = String((a as any)[sortKey] ?? '').localeCompare(String((b as any)[sortKey] ?? ''));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [approvers, filterCardholder, filterApprover, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }
  function sIcon(key: SortKey) { return sortKey !== key ? ' \u21D5' : sortDir === 'asc' ? ' \u2191' : ' \u2193'; }

  const hasFilters = filterCardholder || filterApprover;
  function clearFilters() { setFilterCardholder(''); setFilterApprover(''); }

  const cardholderOptions = useMemo(() => [...new Set(approvers.map(a => a.cardholderName).filter(x => x != null && x !== ''))].sort(), [approvers]);
  const approverNameOptions = useMemo(() => [...new Set(approvers.map(a => a.approverName).filter(x => x != null && x !== ''))].sort(), [approvers]);

  const filteredCardSearch = useMemo(() => {
    if (!cardSearch) return cards.slice(0, 20);
    return cards.filter(c => (c.employeeName || '').toLowerCase().includes(cardSearch.toLowerCase())).slice(0, 20);
  }, [cards, cardSearch]);

  const filteredApprSearch = useMemo(() => {
    if (!apprSearch) return employees.slice(0, 20);
    return employees.filter(e => (e.fullName || '').toLowerCase().includes(apprSearch.toLowerCase()) || (e.employeeNumber || '').toLowerCase().includes(apprSearch.toLowerCase())).slice(0, 20);
  }, [employees, apprSearch]);

  function selectCard(c: CorporateCardListItem) {
    setForm(f => ({ ...f, corporateCardId: c.corporateCardId }));
    setCardSearch(`${c.employeeName} \u2014 \u2022\u2022\u2022\u2022 ${c.lastFourDigits || '????'}`);
    setCardDropdownOpen(false);
  }

  function selectApprover(emp: EmployeeOption) {
    setForm(f => ({ ...f, approverEmployeeId: emp.employeeId }));
    setApprSearch(emp.fullName);
    setApprDropdownOpen(false);
  }

  async function save() {
    try {
      setSaving(true);
      setError('');
      await createApprover({ corporateCardId: form.corporateCardId, approverEmployeeId: form.approverEmployeeId });
      setCreating(false);
      setForm({ corporateCardId: '', approverEmployeeId: '' });
      setCardSearch('');
      setApprSearch('');
      await load();
    } catch (e: any) {
      const b = e.body ? JSON.parse(e.body) : null;
      setError(b?.error || e.message);
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { await deleteApprover(deleteId); setDeleteId(null); await load(); }
    catch (e: any) { setError(e.message); }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error && <ErrorBanner message={error} onDismiss={() => setError('')} />}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h4 className={styles.formTitle}>
          Card Approver Mappings
          {hasFilters && <span style={{ fontSize: 12, color: 'var(--turquoise)', marginLeft: 8 }}>({filteredApprovers.length} of {approvers.length})</span>}
        </h4>
        <div style={{ display: 'flex', gap: 8 }}>
          {hasFilters && <button onClick={clearFilters} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Clear</button>}
          <button onClick={() => setShowFilters(!showFilters)} style={{ fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 6, cursor: 'pointer', background: showFilters ? 'var(--turquoise-light)' : 'var(--gray-100)', color: showFilters ? 'var(--turquoise-dark)' : 'var(--gray-600)', border: '1px solid ' + (showFilters ? 'var(--turquoise)' : 'var(--gray-200)') }}>Filters</button>
          <button className={styles.saveBtn} onClick={() => { setCreating(true); setCardSearch(''); setApprSearch(''); }}>+ Assign Approver</button>
        </div>
      </div>

      {showFilters && (
        <div style={{ marginBottom: 12 }}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%', gap: 10 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Cardholder</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterCardholder} onChange={e => setFilterCardholder(e.target.value)}>
                <option value="">All</option>{cardholderOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Approver</label>
              <select className={styles.fieldInput} style={{ fontSize: 12, width: '100%', boxSizing: 'border-box' }} value={filterApprover} onChange={e => setFilterApprover(e.target.value)}>
                <option value="">All</option>{approverNameOptions.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </Card>
        </div>
      )}

      {creating && (
        <div style={{ marginBottom: 16 }}>
        <Card className={styles.formCard} padding={true}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', width: '100%', gap: 12 }}>
            <div className={styles.field} style={{ position: 'relative' }} ref={cardRef}>
              <label className={styles.fieldLabel}>Card {form.corporateCardId && <span style={{ color: 'var(--green)' }}>{'\u2705'}</span>}</label>
              <input className={styles.fieldInput} value={cardSearch} onChange={e => { setCardSearch(e.target.value); setCardDropdownOpen(true); setForm(f => ({ ...f, corporateCardId: '' })); }} onFocus={() => setCardDropdownOpen(true)} placeholder="Search cardholder..." />
              {cardDropdownOpen && filteredCardSearch.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 200, overflowY: 'auto', background: 'white', border: '1px solid var(--gray-200)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {filteredCardSearch.map(c => (
                    <div key={c.corporateCardId} onClick={() => selectCard(c)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--gray-50)' }} onMouseEnter={e => (e.currentTarget.style.background = 'var(--turquoise-light)')} onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                      <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{c.employeeName}</span> <span style={{ color: 'var(--gray-400)', fontSize: 11 }}>{'\u2022\u2022\u2022\u2022'} {c.lastFourDigits || '????'} ({c.cardIssuer || 'N/A'})</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.field} style={{ position: 'relative' }} ref={apprRef}>
              <label className={styles.fieldLabel}>Approver {form.approverEmployeeId && <span style={{ color: 'var(--green)' }}>{'\u2705'}</span>}</label>
              <input className={styles.fieldInput} value={apprSearch} onChange={e => { setApprSearch(e.target.value); setApprDropdownOpen(true); setForm(f => ({ ...f, approverEmployeeId: '' })); }} onFocus={() => setApprDropdownOpen(true)} placeholder="Search approver..." />
              {apprDropdownOpen && filteredApprSearch.length > 0 && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, maxHeight: 200, overflowY: 'auto', background: 'white', border: '1px solid var(--gray-200)', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {filteredApprSearch.map(emp => (
                    <div key={emp.employeeId} onClick={() => selectApprover(emp)} style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--gray-50)', display: 'flex', justifyContent: 'space-between' }} onMouseEnter={e => (e.currentTarget.style.background = 'var(--turquoise-light)')} onMouseLeave={e => (e.currentTarget.style.background = 'white')}>
                      <span style={{ fontWeight: 600, color: 'var(--navy)' }}>{emp.fullName}</span>
                      <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{emp.employeeNumber}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className={styles.formActions}>
            <button className={styles.cancelBtn} onClick={() => setCreating(false)}>Cancel</button>
            <button className={styles.saveBtn} onClick={save} disabled={saving || !form.corporateCardId || !form.approverEmployeeId}>
              {saving ? 'Saving...' : 'Assign'}
            </button>
          </div>
        </Card>
        </div>
      )}

      <Card padding={false}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('cardholderName')}>Cardholder{sIcon('cardholderName')}</th>
              <th>Card</th>
              <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('approverName')}>Approver{sIcon('approverName')}</th>
              <th style={{ width: 80 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApprovers.map(a => (
              <tr key={a.cardApproverId}>
                <td style={{ fontWeight: 600 }}>{a.cardholderName}</td>
                <td>{'\u2022\u2022\u2022\u2022'} {a.lastFourDigits || '????'} ({a.cardIssuer || 'N/A'})</td>
                <td>{a.approverName}</td>
                <td><button className={styles.deleteBtn} onClick={() => setDeleteId(a.cardApproverId)}>Remove</button></td>
              </tr>
            ))}
            {filteredApprovers.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: 'center', padding: 20, color: 'var(--gray-400)' }}>{hasFilters ? 'No mappings match filters.' : 'No approver mappings yet.'}</td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <ConfirmDialog open={!!deleteId} title="Remove Approver" message="Remove this approver mapping?" confirmLabel="Remove" confirmVariant="danger" onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
    </div>
  );
}
